import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useHistory } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import {
  getActiveRadioQueue,
  getActiveRadioTracks,
  HITLAB_RADIO_ROUTE,
  RADIO_QUEUE_KINDS,
  RADIO_ROTATION_QUEUE,
} from './hitLabRadioTracks';
import './hitLab.css';

const RADIO_FEEDBACK_REWARD_JGK = 5;
const RADIO_FEEDBACK_MIN_SECONDS = 45;
const RADIO_FEEDBACK_MIN_PERCENT = 35;

const FEEDBACK_FIELDS = [
  {
    name: 'hookReaction',
    label: 'Did the hook catch you?',
    options: [
      ['caught_me_fast', 'Caught me fast'],
      ['grew_on_me', 'Grew on me'],
      ['did_not_catch_me_yet', 'Not yet'],
    ],
  },
  {
    name: 'replayValue',
    label: 'Would you run it back?',
    options: [
      ['run_it_back', 'Run it back'],
      ['maybe_later', 'Maybe later'],
      ['probably_skip', 'Probably skip'],
    ],
  },
  {
    name: 'artistIdentity',
    label: 'Is the artist identity clear?',
    options: [
      ['clear', 'Clear'],
      ['somewhat_clear', 'Somewhat'],
      ['not_clear_yet', 'Not clear yet'],
    ],
  },
  {
    name: 'fanAction',
    label: 'What action would you take?',
    options: [
      ['would_follow', 'Follow'],
      ['would_tip_support', 'Tip / support'],
      ['would_share', 'Share'],
      ['would_not_act_yet', 'Not yet'],
    ],
  },
  {
    name: 'vibeFit',
    label: 'Where does this record fit?',
    options: [
      ['radio_ready', 'Radio ready'],
      ['playlist_ready', 'Playlist ready'],
      ['club_street_energy', 'Club / street'],
      ['needs_more_polish', 'Needs polish'],
    ],
  },
];

const STANDOUT_OPTIONS = [
  ['hook', 'Hook'],
  ['beat', 'Beat'],
  ['voice', 'Voice'],
  ['energy', 'Energy'],
  ['lyrics', 'Lyrics'],
  ['vibe', 'Vibe'],
  ['none_yet', 'None yet'],
];

const initialFeedbackForm = {
  hookReaction: '',
  replayValue: '',
  artistIdentity: '',
  fanAction: '',
  vibeFit: '',
  standoutReason: 'none_yet',
  comment: '',
};

const formatTime = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

const clampIndex = (index, length) => {
  if (!length) return 0;
  const next = Number(index);
  if (!Number.isFinite(next)) return 0;
  return ((Math.trunc(next) % length) + length) % length;
};

const getRewardMessage = (feedback = {}) => {
  const amount = Number(feedback.rewardAmountJgk || RADIO_FEEDBACK_REWARD_JGK);
  if (feedback.message) return feedback.message;
  if (feedback.rewardStatus === 'credited') {
    return `Feedback locked in. +${amount} JGK earned.`;
  }
  if (feedback.rewardStatus === 'already_credited') {
    return 'You already gave feedback on this record. Your signal is locked in.';
  }
  if (feedback.rewardStatus === 'pending') {
    return 'Feedback locked in. Reward pending.';
  }
  return 'Feedback could not be saved. Please try again.';
};

function FeedbackChoiceGroup({ field, value, onChange }) {
  return (
    <fieldset className="hitlab-radio-feedback-group">
      <legend>{field.label}</legend>
      <div role="radiogroup" aria-label={field.label}>
        {field.options.map(([optionValue, label]) => (
          <label
            key={optionValue}
            className={`hitlab-radio-feedback-choice ${
              value === optionValue ? 'is-selected' : ''
            }`}
          >
            <input
              type="radio"
              name={field.name}
              value={optionValue}
              checked={value === optionValue}
              onChange={() => onChange(field.name, optionValue)}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default function HitLabRadio({ queue = RADIO_ROTATION_QUEUE }) {
  const audioRef = useRef(null);
  const pendingAutoPlayRef = useRef(false);
  const feedbackCtaRef = useRef(null);
  const feedbackDialogRef = useRef(null);
  const history = useHistory();
  const { player } = useContext(AuthContext);
  const activeQueue = useMemo(() => getActiveRadioQueue(queue), [queue]);
  const activeTrackCount = useMemo(() => getActiveRadioTracks(queue).length, [queue]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [duration, setDuration] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState(initialFeedbackForm);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackResult, setFeedbackResult] = useState(null);
  const [feedbackError, setFeedbackError] = useState('');

  const currentItem = activeQueue[clampIndex(currentIndex, activeQueue.length)] || null;
  const isTrack = currentItem?.kind === RADIO_QUEUE_KINDS.TRACK;
  const isBumper = currentItem?.kind === RADIO_QUEUE_KINDS.BUMPER;
  const progress = duration > 0 ? Math.min(100, (elapsed / duration) * 100) : 0;
  const listenPercent = duration > 0 ? Math.min(100, (elapsed / duration) * 100) : 0;
  const feedbackUnlocked =
    isTrack &&
    (elapsed >= RADIO_FEEDBACK_MIN_SECONDS ||
      listenPercent >= RADIO_FEEDBACK_MIN_PERCENT);
  const feedbackUnlockProgress = Math.min(
    100,
    Math.max(
      (elapsed / RADIO_FEEDBACK_MIN_SECONDS) * 100,
      listenPercent / RADIO_FEEDBACK_MIN_PERCENT * 100,
    ),
  );
  const isFeedbackComplete = FEEDBACK_FIELDS.every(
    (field) => Boolean(feedbackForm[field.name]),
  );
  const feedbackLoginHref = `/login?from=${encodeURIComponent(HITLAB_RADIO_ROUTE)}`;

  const resetAudioState = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.load?.();
    } catch (err) {
      // Browser media state can reject seeks; keep the UI usable.
    }
  }, []);

  const playCurrent = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !currentItem) return false;
    setErrorMessage('');
    setIsLoading(true);
    try {
      const result = audio.play();
      if (result && typeof result.then === 'function') {
        await result;
      }
      setIsPlaying(true);
      setIsLoading(false);
      return true;
    } catch (err) {
      setIsPlaying(false);
      setIsLoading(false);
      setErrorMessage('This audio item could not start. Try the next item.');
      return false;
    }
  }, [currentItem]);

  const goToIndex = useCallback(
    (nextIndex, { keepPlaying = false } = {}) => {
      if (!activeQueue.length) return;
      pendingAutoPlayRef.current = keepPlaying;
      setCurrentIndex(clampIndex(nextIndex, activeQueue.length));
    },
    [activeQueue.length],
  );

  const goNext = useCallback(
    ({ keepPlaying = isPlaying } = {}) => {
      goToIndex(currentIndex + 1, { keepPlaying });
    },
    [currentIndex, goToIndex, isPlaying],
  );

  const goPrevious = useCallback(() => {
    goToIndex(currentIndex - 1, { keepPlaying: isPlaying });
  }, [currentIndex, goToIndex, isPlaying]);

  useEffect(() => {
    setCurrentIndex((prev) => clampIndex(prev, activeQueue.length));
  }, [activeQueue.length]);

  useEffect(() => {
    resetAudioState();
    setElapsed(0);
    setDuration(0);
    setErrorMessage('');
    setIsLoading(false);
    setIsPlaying(false);
    if (pendingAutoPlayRef.current) {
      pendingAutoPlayRef.current = false;
      window.setTimeout(() => {
        void playCurrent();
      }, 0);
    }
  }, [currentItem?.id, playCurrent, resetAudioState]);

  useEffect(() => () => resetAudioState(), [resetAudioState]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    void playCurrent();
  };

  const handleLoadedMetadata = () => {
    const nextDuration = Number(audioRef.current?.duration);
    setDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
    setIsLoading(false);
  };

  const handleTimeUpdate = () => {
    const nextElapsed = Number(audioRef.current?.currentTime);
    setElapsed(Number.isFinite(nextElapsed) ? nextElapsed : 0);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (activeQueue.length > 1) {
      goNext({ keepPlaying: true });
    }
  };

  const handleError = () => {
    setIsPlaying(false);
    setIsLoading(false);
    setErrorMessage('This audio item could not load. Use next to keep listening.');
  };

  const resetFeedbackState = useCallback(() => {
    setFeedbackForm(initialFeedbackForm);
    setFeedbackSubmitting(false);
    setFeedbackResult(null);
    setFeedbackError('');
  }, []);

  const closeFeedbackSheet = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackSubmitting(false);
    window.setTimeout(() => {
      feedbackCtaRef.current?.focus?.();
    }, 0);
  }, []);

  const openFeedbackSheet = () => {
    resetFeedbackState();
    setFeedbackOpen(true);
  };

  const updateFeedbackForm = (field, value) => {
    setFeedbackForm((current) => ({
      ...current,
      [field]: field === 'comment' ? String(value || '').slice(0, 500) : value,
    }));
  };

  const goToLogin = () => {
    history.push(feedbackLoginHref);
  };

  const submitFeedback = async () => {
    if (!player?.token || !currentItem || !isTrack) return;
    if (!feedbackUnlocked || !isFeedbackComplete) return;

    setFeedbackSubmitting(true);
    setFeedbackError('');
    setFeedbackResult(null);
    try {
      const response = await axiosConn.post(
        'hitlab/radio/feedback',
        {
          trackId: currentItem.id,
          listenSession: {
            listenedSeconds: Math.floor(elapsed),
            listenPercent: Math.round(listenPercent),
          },
          feedback: feedbackForm,
          clientMeta: {
            source: 'joe_galaxy_radio',
            route: HITLAB_RADIO_ROUTE,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${player.token}`,
          },
        },
      );
      setFeedbackResult(response?.data?.data?.feedback || null);
    } catch (err) {
      setFeedbackError(
        err?.response?.data?.message ||
          'Feedback could not be saved. Please try again.',
      );
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  useEffect(() => {
    setFeedbackOpen(false);
    resetFeedbackState();
  }, [currentItem?.id, resetFeedbackState]);

  useEffect(() => {
    if (!feedbackOpen) return undefined;
    window.setTimeout(() => {
      feedbackDialogRef.current?.focus?.();
    }, 0);
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeFeedbackSheet();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeFeedbackSheet, feedbackOpen]);

  if (!activeQueue.length || activeTrackCount <= 0) {
    return (
      <main className="hitlab-page hitlab-radio-page">
        <Helmet>
          <title>Joe Galaxy Radio Rotation | Preparing</title>
          <meta
            name="description"
            content="Joe Galaxy Radio Rotation is preparing for launch."
          />
        </Helmet>
        <section className="hitlab-shell hitlab-radio-empty">
          <p className="hitlab-kicker">JG RADIO ROTATION</p>
          <h1>Joe Galaxy Radio Rotation is preparing for launch.</h1>
          <p>
            Rights-clean records will appear here as the rotation opens.
          </p>
          <Link className="hitlab-secondary-link" to="/hitlab">
            Back to HitLab
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="hitlab-page hitlab-radio-page">
      <Helmet>
        <title>Joe Galaxy Radio Rotation | JG HitLab</title>
        <meta
          name="description"
          content="Listen to the active Joe Galaxy Radio Rotation inside JG Universe."
        />
      </Helmet>

      <section className="hitlab-radio-hero">
        <div className="hitlab-shell hitlab-radio-shell">
          <div className="hitlab-radio-copy">
            <p className="hitlab-kicker">JG RADIO ROTATION</p>
            <h1>Joe Galaxy Radio Rotation</h1>
            <p className="hitlab-lede">
              Records are already in rotation inside JG Universe.
            </p>
            <p>
              HitLab is the gate for artists with one original record strong
              enough to earn the next move. Placement cannot be bought.
            </p>
          </div>

          <article
            className={`hitlab-radio-player ${isCompact ? 'is-compact' : ''}`}
            aria-label="Joe Galaxy Radio Rotation player"
          >
            <div className="hitlab-radio-player-top">
              <div>
                <span className="hitlab-radio-live-dot" aria-hidden="true" />
                <span className="hitlab-radio-label">
                  Joe Galaxy Radio Rotation
                </span>
              </div>
              <button
                type="button"
                className="hitlab-radio-icon-btn"
                onClick={() => setIsCompact((value) => !value)}
                aria-label={isCompact ? 'Expand radio player' : 'Minimize radio player'}
              >
                {isCompact ? 'Expand' : 'Minimize'}
              </button>
            </div>

            <div className="hitlab-radio-now">
              <div className="hitlab-radio-art-wrap">
                {isTrack ? (
                  <img
                    className="hitlab-radio-cover"
                    src={currentItem.coverArtUrl}
                    alt={`Cover art for ${currentItem.songTitle} by ${currentItem.artistName}`}
                    width="420"
                    height="420"
                    decoding="async"
                  />
                ) : null}
                {isBumper ? (
                  <div
                    className="hitlab-radio-bumper-visual"
                    data-testid="hitlab-radio-bumper-visual"
                    aria-label="Station bumper visual"
                  >
                    <span>JG</span>
                    <strong>ON AIR</strong>
                  </div>
                ) : null}
              </div>

              <div className="hitlab-radio-details">
                <p className="hitlab-radio-state">
                  {isBumper ? 'Station sweep' : 'Now playing'}
                </p>
                <h2>{isTrack ? currentItem.songTitle : currentItem.label}</h2>
                <p className="hitlab-radio-artist">
                  {isTrack ? currentItem.artistName : currentItem.title}
                </p>
                <div
                  className={`hitlab-radio-visualizer ${isPlaying ? 'is-playing' : ''}`}
                  aria-hidden="true"
                >
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>

            <audio
              ref={audioRef}
              src={currentItem.audioUrl}
              preload="metadata"
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              onError={handleError}
              aria-label="Radio playback audio"
            />

            <div className="hitlab-radio-controls" aria-label="Radio controls">
              <button
                type="button"
                className="hitlab-radio-control-btn"
                onClick={goPrevious}
                aria-label="Previous radio item"
              >
                Previous
              </button>
              <button
                type="button"
                className="hitlab-radio-play-btn"
                onClick={togglePlayback}
                aria-label={isPlaying ? 'Pause radio rotation' : 'Play radio rotation'}
              >
                {isLoading ? 'Loading' : isPlaying ? 'Pause' : 'Play'}
              </button>
              <button
                type="button"
                className="hitlab-radio-control-btn"
                onClick={() => goNext()}
                aria-label="Next radio item"
              >
                Next
              </button>
            </div>

            <div className="hitlab-radio-progress-row">
              <span>{formatTime(elapsed)}</span>
              <div
                className="hitlab-radio-progress"
                role="progressbar"
                aria-label="Radio playback progress"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={Math.round(progress)}
              >
                <span style={{ width: `${progress}%` }} />
              </div>
              <span>{formatTime(duration)}</span>
            </div>

            {errorMessage ? (
              <div className="hitlab-radio-error" role="alert">
                {errorMessage}
              </div>
            ) : null}

            {isTrack ? (
              <div className="hitlab-radio-action-rail" aria-label="Fan actions">
                <button
                  type="button"
                  ref={feedbackCtaRef}
                  onClick={openFeedbackSheet}
                  aria-label="Give feedback and earn JGK"
                >
                  Give Feedback <span>Earn JGK</span>
                </button>
                <button type="button" disabled aria-label="Tip with JGK coming next">
                  Tip with JGK <span>Coming next</span>
                </button>
                <button
                  type="button"
                  disabled
                  aria-label="Buy or Support with JGK coming next"
                >
                  Buy / Support with JGK <span>Coming next</span>
                </button>
                {currentItem.artistInstagramUrl ? (
                  <a
                    href={currentItem.artistInstagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${currentItem.artistName} Instagram`}
                  >
                    Instagram
                  </a>
                ) : null}
              </div>
            ) : null}
          </article>
        </div>
      </section>

      {feedbackOpen && isTrack ? (
        <div
          className="hitlab-radio-feedback-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeFeedbackSheet();
          }}
        >
          <section
            className="hitlab-radio-feedback-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hitlab-radio-feedback-title"
            tabIndex="-1"
            ref={feedbackDialogRef}
          >
            <button
              type="button"
              className="hitlab-radio-feedback-close"
              onClick={closeFeedbackSheet}
              aria-label="Close feedback"
            >
              Close
            </button>

            <div className="hitlab-radio-feedback-track">
              <img
                src={currentItem.coverArtUrl}
                alt={`Cover art for ${currentItem.songTitle} by ${currentItem.artistName}`}
                width="96"
                height="96"
              />
              <div>
                <p className="hitlab-kicker">Fan action signal</p>
                <h2 id="hitlab-radio-feedback-title">
                  Give feedback. Earn {RADIO_FEEDBACK_REWARD_JGK} JGK.
                </h2>
                <p>
                  {currentItem.songTitle} by {currentItem.artistName}
                </p>
              </div>
            </div>

            {!player?.token ? (
              <div className="hitlab-radio-feedback-auth">
                <h3>Sign in to give feedback and earn JGK.</h3>
                <p>
                  Your signal helps records earn their next move inside Joe
                  Galaxy Radio.
                </p>
                <button type="button" className="hitlab-primary-btn" onClick={goToLogin}>
                  Sign In
                </button>
                <Link className="hitlab-secondary-link" to={feedbackLoginHref}>
                  Continue to login
                </Link>
              </div>
            ) : !feedbackUnlocked ? (
              <div className="hitlab-radio-feedback-locked">
                <h3>Listen a little longer to unlock rewarded feedback.</h3>
                <p>
                  {formatTime(elapsed)} / {formatTime(RADIO_FEEDBACK_MIN_SECONDS)}
                </p>
                <div
                  className="hitlab-radio-feedback-unlock"
                  role="progressbar"
                  aria-label="Rewarded feedback unlock progress"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={Math.round(feedbackUnlockProgress)}
                >
                  <span style={{ width: `${feedbackUnlockProgress}%` }} />
                </div>
              </div>
            ) : feedbackResult ? (
              <div className="hitlab-radio-feedback-result" role="status">
                <h3>{getRewardMessage(feedbackResult)}</h3>
                <p>
                  Your signal helps records earn their next move inside Joe
                  Galaxy Radio.
                </p>
              </div>
            ) : (
              <form
                className="hitlab-radio-feedback-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void submitFeedback();
                }}
              >
                <p>
                  Your signal helps records earn their next move inside Joe
                  Galaxy Radio.
                </p>
                {FEEDBACK_FIELDS.map((field) => (
                  <FeedbackChoiceGroup
                    key={field.name}
                    field={field}
                    value={feedbackForm[field.name]}
                    onChange={updateFeedbackForm}
                  />
                ))}

                <details className="hitlab-radio-feedback-extra">
                  <summary>Add optional detail</summary>
                  <fieldset className="hitlab-radio-feedback-group">
                    <legend>What stood out?</legend>
                    <div role="radiogroup" aria-label="What stood out?">
                      {STANDOUT_OPTIONS.map(([optionValue, label]) => (
                        <label
                          key={optionValue}
                          className={`hitlab-radio-feedback-choice ${
                            feedbackForm.standoutReason === optionValue
                              ? 'is-selected'
                              : ''
                          }`}
                        >
                          <input
                            type="radio"
                            name="standoutReason"
                            value={optionValue}
                            checked={feedbackForm.standoutReason === optionValue}
                            onChange={() =>
                              updateFeedbackForm('standoutReason', optionValue)
                            }
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <label className="hitlab-radio-feedback-comment">
                    Optional comment
                    <textarea
                      value={feedbackForm.comment}
                      maxLength="500"
                      onChange={(event) =>
                        updateFeedbackForm('comment', event.target.value)
                      }
                      placeholder="A short note for the signal."
                    />
                  </label>
                </details>

                {feedbackError ? (
                  <div className="hitlab-radio-error" role="alert">
                    {feedbackError}
                  </div>
                ) : null}

                <button
                  type="submit"
                  className="hitlab-primary-btn"
                  disabled={!isFeedbackComplete || feedbackSubmitting}
                >
                  {feedbackSubmitting
                    ? 'Submitting'
                    : 'Submit Feedback + Earn JGK'}
                </button>
              </form>
            )}
          </section>
        </div>
      ) : null}
    </main>
  );
}
